import * as React from "react";
import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";

/**
 * PRTL-1356: tokens expire after 7 days, so re-mint once the current one is
 * over a day old (sliding window). Checked on mount and whenever the window
 * regains focus; the server revokes the replaced session as part of the
 * refresh.
 */
const REFRESH_AFTER_MS = 24 * 60 * 60 * 1000;

function tokenIssuedAtMs(token: string): number | null {
    try {
        // JWT payloads are base64url — convert to base64 and pad before atob.
        const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
        const payload = JSON.parse(atob(padded)) as { iat?: number };
        return typeof payload.iat === "number" ? payload.iat * 1000 : null;
    } catch {
        return null;
    }
}

export const SessionRefresh = () => {
    const token = React.useSyncExternalStore(tokenManager.subscribe, tokenManager.get);
    const refreshMutation = trpc.auth.refreshToken.useMutation({
        onSuccess: ({ token: newToken }) => tokenManager.set(newToken),
    });
    const inFlight = React.useRef(false);

    React.useEffect(() => {
        if (!token) return;

        const maybeRefresh = () => {
            // Masquerade sessions cannot be extended (server rejects too).
            if (inFlight.current || tokenManager.isMasquerading()) return;
            const issuedAt = tokenIssuedAtMs(token);
            if (issuedAt === null || Date.now() - issuedAt < REFRESH_AFTER_MS) return;
            inFlight.current = true;
            refreshMutation.mutate(undefined, {
                onSettled: () => {
                    inFlight.current = false;
                },
            });
        };

        maybeRefresh();
        window.addEventListener("focus", maybeRefresh);
        return () => window.removeEventListener("focus", maybeRefresh);
        // refreshMutation identity changes every render; token is the real dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return null;
};
